import { motion } from "framer-motion";

/**
 * Shared chrome used by every product mock dashboard.
 *
 * Renders a glassy, dark, browser-like frame with:
 *  - 3-dot window controls
 *  - faux domain bar
 *  - optional tab strip (e.g. "Overview / Requests / Audit")
 *  - subtle accent-tinted glow + bottom shadow
 *
 * Children are the dashboard widgets themselves.
 */
export default function MockFrame({
  domain,
  accent,
  tabs,
  activeTab = 0,
  children,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      <div
        className="pointer-events-none absolute -inset-10 -z-10 rounded-[40px] blur-3xl"
        style={{ backgroundColor: accent.glow, opacity: 0.55 }}
        aria-hidden
      />

      <div
        className="pointer-events-none absolute -inset-px rounded-[22px] opacity-60"
        style={{
          background: `linear-gradient(135deg, ${accent.border}, transparent 35%, transparent 65%, ${accent.border})`,
        }}
        aria-hidden
      />

      <div
        className="relative overflow-hidden rounded-[18px] border bg-[#0b1118]/95 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-xl sm:rounded-[22px]"
        style={{ borderColor: "rgba(255,255,255,0.08)" }}
      >
        <div
          className="flex items-center gap-2 border-b px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5"
          style={{ borderColor: "rgba(255,255,255,0.06)" }}
        >
          <div className="hidden items-center gap-1.5 sm:flex">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>
          <div
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md border px-2 py-1 sm:px-2.5"
            style={{
              borderColor: "rgba(255,255,255,0.06)",
              backgroundColor: "rgba(255,255,255,0.02)",
            }}
          >
            <span
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: accent.primary, boxShadow: `0 0 8px ${accent.primary}` }}
            />
            <p className="truncate text-[10px] font-medium tracking-tight text-white/50 sm:text-[10.5px]">
              {domain}
            </p>
          </div>
          <span className="hidden items-center gap-1 text-[10px] font-medium text-white/35 sm:inline-flex">
            <span
              className="h-1.5 w-1.5 animate-pulse rounded-full"
              style={{ backgroundColor: "#34d399" }}
            />
            Live
          </span>
        </div>

        {tabs && tabs.length > 0 && (
          <div
            className="flex items-center gap-1 overflow-x-auto border-b px-2.5 py-1.5 [scrollbar-width:none] sm:px-3 [&::-webkit-scrollbar]:hidden"
            style={{ borderColor: "rgba(255,255,255,0.06)" }}
          >
            {tabs.map((t, i) => {
              const active = i === activeTab;
              return (
                <span
                  key={t}
                  className="shrink-0 rounded-md px-2 py-1 text-[10px] font-semibold transition-colors sm:px-2.5 sm:text-[10.5px]"
                  style={
                    active
                      ? {
                          color: accent.light,
                          backgroundColor: accent.bg,
                          boxShadow: `inset 0 0 0 1px ${accent.border}`,
                        }
                      : { color: "rgba(255,255,255,0.45)" }
                  }
                >
                  {t}
                </span>
              );
            })}
          </div>
        )}

        <div className="p-3 sm:p-4">{children}</div>
      </div>
    </motion.div>
  );
}
